import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
interface FormData {
  email: string;
  type: 'user' | 'merchant' | 'enterprise';
  ageConfirm: boolean;
}

const WhitepaperPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  
  const [formData, setFormData] = useState<FormData>({
       email: '',
       type: 'user',
       ageConfirm: false
     });
   const [isSubmitting, setIsSubmitting] = useState(false);
     const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    document.title = 'Whitepaper — Earn-Time Loyalty Protocol on Polygon | Rewardly';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('article:')) {
          el.setAttribute('property', name);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Complete technical blueprint. 38+ smart contracts, 7 layers, 120B RWLY tokens. Earn-time commitment architecture on Polygon. Patent pending.');
    setMeta('og:title', 'Rewardly Protocol Whitepaper — Technical Blueprint');
    setMeta('og:description', 'Seven-layer smart contract architecture. 120B RWLY. Earn-time commitment on Polygon. Patent pending.');
    setMeta('og:type', 'website');
    setMeta('og:url', 'https://rewardly.live/whitepaper');
    setMeta('og:image', 'https://rewardly.live/og-image.png');
    setMeta('og:site_name', 'Rewardly Protocol');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@Rewardly_io');
    setMeta('twitter:title', 'Rewardly Protocol Whitepaper — Technical Blueprint');
    setMeta('twitter:description', 'Seven-layer smart contract architecture. 120B RWLY. Earn-time commitment on Polygon. Patent pending.');
    setMeta('twitter:image', 'https://rewardly.live/og-image.png');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://rewardly.live/whitepaper');
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  const validateEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };
      const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        
        // Validate email
        if (!validateEmail(formData.email)) {
          setSubmitMessage('Please enter a valid email address');
          return;
        }
    
        // Validate age confirmation
        if (!formData.ageConfirm) {
          setSubmitMessage('Please confirm you are 18 or older');
          return;
        }
        setIsSubmitting(true);
        setSubmitMessage('');
        try {
          const response = await fetch('https://5g72iuzmj9.execute-api.us-east-1.amazonaws.com/dev/save', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              email: formData.email,
              user_type: formData.type
            })
          });
    
          const raw = await response.json();
          let data: any;
          if (raw.body && typeof raw.body === 'string') {
            try { data = JSON.parse(raw.body); } catch { data = raw; }
          } else { data = raw; }
          const statusCode = raw.statusCode || response.status;
          if (statusCode === 200 && !data.error) {
            if (data.new_subscription) {
              setSubmitMessage('Welcome! You\'re on the list. Check your email for confirmation. Follow @Rewardly_io for updates.');
            } else {
              setSubmitMessage('You\'re already on the waitlist! We\'ll email you when early access opens.');
            }
            setFormData({ email: '', type: 'user', ageConfirm: false });
            setTimeout(() => {
              setModalOpen(false);
              setSubmitMessage('');
            }, 3000);
          } else {
            setSubmitMessage(data.error || 'Failed to join waitlist. Please try again.');
          }
        } catch (error) {
          console.error('Error submitting email:', error);
          setSubmitMessage('An error occurred. Please try again.');
        } finally {
          setIsSubmitting(false);
        }
      };
    
      const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
        const target = e.target as HTMLInputElement;
        const { name, value, type, checked } = target;
        setFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error message when user starts typing
        if (submitMessage) setSubmitMessage('');
      };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=Outfit:wght@400;500;600;700;800&display=swap');
        
        :root {
          --white: #FFFFFF;
          --off-white: #F8FAFC;
          --gray-100: #F1F5F9;
          --gray-200: #E2E8F0;
          --gray-400: #94A3B8;
          --gray-500: #64748B;
          --gray-900: #0F172A;
          --blue-600: #2563EB;
          --blue-700: #1D4ED8;
          --gold: #D4A853;
          --gold-dark: #B8923A;
          --green-600: #059669;
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        
        body {
          font-family: 'Inter', -apple-system, sans-serif;
          background: var(--white);
          color: var(--gray-900);
          line-height: 1.7;
          -webkit-font-smoothing: antialiased;
        }

        h1, h2, h3, .section-title, .stat-value, .inversion-title {
          font-family: 'Outfit', 'Inter', -apple-system, sans-serif;
        }

body, html {
  overflow-x: hidden;
}
        .container {
          max-width: 1140px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .container-narrow {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 24px;
        }

        nav {
          position: fixed;
          top: 0;
          width: 100%;
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--gray-200);
          z-index: 1000;
        }

        .nav-inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 72px;
        }

        .nav-menu {
          display: flex;
          align-items: center;
          gap: 32px;
        }

        .nav-menu > a, .dropdown > a {
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          font-size: 15px;
          transition: color 0.2s;
          cursor: pointer;
        }

        .nav-menu > a:hover, .dropdown > a:hover { color: var(--blue-600); }

        .dropdown {
          position: relative;
        }

        .dropdown-toggle {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .dropdown-toggle::after {
          content: '▼';
          font-size: 10px;
          transition: transform 0.2s;
        }

        .dropdown:hover .dropdown-toggle::after {
          transform: rotate(180deg);
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          padding: 8px 0;
          min-width: 180px;
          margin-top: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.2s;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        }

        .dropdown:hover .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-menu a {
          display: block;
          padding: 10px 20px;
          color: var(--gray-900);
          text-decoration: none;
          font-size: 14px;
          transition: background 0.2s;
        }

        .dropdown-menu a:hover {
          background: var(--gray-100);
        }

        .dropdown.nested {
          position: relative;
        }

        .dropdown.nested > a {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          cursor: pointer;
        }

        .dropdown.nested > a::after {
          content: '→';
          font-size: 12px;
          margin-left: auto;
          transition: transform 0.2s;
        }

        .dropdown.nested:hover > a::after {
          transform: translateX(2px);
        }

        .dropdown > .dropdown-menu > .dropdown.nested > .dropdown-menu {
          position: absolute;
          top: 0;
          left: 100%;
          margin-top: 0;
          margin-left: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateX(-10px);
          transition: all 0.2s;
          pointer-events: none;
        }

        .dropdown > .dropdown-menu > .dropdown.nested:hover > .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateX(0);
          pointer-events: auto;
        }

        .nav-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s;
          border: none;
          cursor: pointer;
        }

        .btn-primary { background: var(--blue-600); color: var(--white); }
        .btn-primary:hover { background: var(--blue-700); }
        .btn-outline { background: transparent; color: var(--gray-900); border: 1px solid var(--gray-200); }
        .btn-outline:hover { border-color: var(--blue-600); color: var(--blue-600); }
        .btn-gold { background: var(--gold); color: var(--gray-900); }
        .btn-gold:hover { background: var(--gold-dark); }
        .btn-secondary { background: var(--white); color: var(--gray-900); border: 1px solid var(--gray-200); }
        .btn-secondary:hover { background: var(--gray-100); }

        .mobile-toggle {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
        }
        .mobile-toggle span { width: 22px; height: 2px; background: var(--gray-900); }

        .mobile-menu {
          display: none;
          position: fixed;
          top: 72px;
          left: 0;
          right: 0;
          background: var(--white);
          border-bottom: 1px solid var(--gray-200);
          padding: 16px 24px;
          z-index: 999;
        }
        .mobile-menu.active { display: block; }
        .mobile-menu a {
          display: block;
          padding: 12px 0;
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          border-bottom: 1px solid var(--gray-100);
        }
        .mobile-menu .mobile-section {
          font-size: 12px;
          color: var(--gray-500);
          text-transform: uppercase;
          margin-top: 16px;
          margin-bottom: 8px;
        }

        .modal-overlay {
          display: none;
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 2000;
          align-items: center;
          justify-content: center;
        }
        .modal-overlay.active { display: flex; }
        .modal {
          background: var(--white);
          border-radius: 16px;
          padding: 32px;
          width: 100%;
          max-width: 440px;
          margin: 24px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        }
        .modal h3 { font-size: 24px; font-weight: 700; margin-bottom: 24px; }
        .modal-field { margin-bottom: 20px; }
        .modal-field label { display: block; font-size: 14px; font-weight: 500; color: var(--gray-500); margin-bottom: 8px; }
        .modal-field input, .modal-field select {
          width: 100%; padding: 14px 16px; border: 1px solid var(--gray-200);
          border-radius: 8px; font-size: 15px; font-family: inherit;
        }
        .modal-field input:focus, .modal-field select:focus { outline: none; border-color: var(--blue-600); }
        .modal-checkbox { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 24px; }
        .modal-checkbox input { width: 18px; height: 18px; margin-top: 2px; accent-color: var(--blue-600); }
        .modal-checkbox span { font-size: 14px; color: var(--gray-500); line-height: 1.5; }
        .modal-buttons { display: flex; gap: 12px; }
        .modal-buttons .btn { flex: 1; justify-content: center; padding: 14px 20px; }

        .hero {
          padding: 140px 0 60px;
          background: var(--off-white);
          border-bottom: 1px solid var(--gray-200);
          text-align: center;
        }

        .hero-label {
          display: inline-block;
          background: var(--white);
          border: 1px solid var(--gray-200);
          padding: 8px 20px;
          border-radius: 30px;
          font-size: 13px;
          font-weight: 600;
          color: var(--gray-500);
          margin-bottom: 24px;
        }

       

        .hero-subtitle {
          font-family: 'Crimson Text', serif;
          font-size: 19px;
          font-style: italic;
          color: var(--gray-500);
          max-width: 740px;
          margin: 0 auto 16px;
          line-height: 1.6;
          letter-spacing: 0.3px;
        }

        .hero-meta {
          font-size: 14px;
          color: var(--gray-400);
        }

        .toc-section {
          padding: 48px 0;
          border-bottom: 1px solid var(--gray-200);
        }

        .toc-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        .toc-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: var(--off-white);
          border-radius: 8px;
          text-decoration: none;
          color: var(--gray-900);
          font-weight: 500;
          font-size: 14px;
          transition: all 0.2s;
        }

        .toc-item:hover {
          background: var(--blue-600);
          color: var(--white);
        }

        .toc-number {
          font-weight: 700;
          color: var(--gold);
        }

        .toc-item:hover .toc-number { color: var(--white); }

        .content-section {
          padding: 64px 0;
          border-bottom: 1px solid var(--gray-200);
        }

        .content-section:last-of-type { border-bottom: none; }

        .section-number {
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--blue-600);
          margin-bottom: 8px;
        }

        .section-title {
          font-size: clamp(28px, 4vw, 36px);
          font-weight: 800;
          margin-bottom: 12px;
        }

        .section-intro {
          font-size: 17px;
          color: var(--gray-500);
          margin-bottom: 32px;
          max-width: 640px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin: 32px 0;
        }

        .stat-card {
          background: var(--off-white);
          padding: 24px;
          border-radius: 12px;
          text-align: center;
          border-left: 3px solid var(--gold);
        }

        .stat-value {
          font-size: 28px;
          font-weight: 800;
          color: var(--gray-900);
        }

        .stat-label {
          font-size: 13px;
          color: var(--gray-500);
          margin-top: 4px;
        }

        .two-col {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
          margin: 32px 0;
        }

        .content-card {
          background: var(--off-white);
          padding: 28px;
          border-radius: 12px;
        }

        .content-card h3 {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 16px;
          color: var(--gray-900);
        }

        .content-card ul {
          list-style: none;
        }

        .content-card ul li {
          padding: 6px 0;
          font-size: 15px;
          color: var(--gray-500);
        }

        .content-card ul li::before {
          content: "→ ";
          color: var(--green-600);
          font-weight: 600;
        }

        .token-section {
          background: var(--gray-900);
          color: var(--white);
          padding: 64px 0;
        }

        .token-section .section-number { color: var(--gold); }
        .token-section .section-title { color: var(--white); }
        .token-section .section-intro { color: rgba(255,255,255,0.7); }

        .token-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .token-total {
          font-size: 56px;
          font-weight: 800;
          color: var(--gold);
        }

        .token-label {
          font-size: 16px;
          color: rgba(255,255,255,0.7);
        }

        .distribution-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .distribution-card {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          padding: 28px;
          border-radius: 12px;
        }

        .distribution-card h3 {
          font-size: 16px;
          font-weight: 600;
          color: rgba(255,255,255,0.9);
          margin-bottom: 8px;
        }

        .distribution-percent {
          font-size: 32px;
          font-weight: 800;
          color: var(--gold);
          margin-bottom: 16px;
        }

        .distribution-desc {
          font-size: 14px;
          color: rgba(255,255,255,0.6);
          line-height: 1.6;
        }

        .regional-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin: 32px 0;
        }

        .regional-card {
          background: var(--off-white);
          padding: 24px;
          border-radius: 12px;
          text-align: center;
        }

        .regional-name {
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .regional-multiplier {
          display: inline-block;
          background: var(--gold);
          color: var(--white);
          padding: 4px 16px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 700;
        }

        .fee-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
          margin: 32px 0;
        }

        .fee-card {
          background: var(--off-white);
          padding: 28px;
          border-radius: 12px;
        }

        .fee-card h3 {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 2px solid var(--gold);
        }

        .fee-row {
          display: flex;
          justify-content: space-between;
          padding: 10px 0;
          border-bottom: 1px solid var(--gray-200);
        }

        .fee-row:last-child { border-bottom: none; }

        .fee-name { color: var(--gray-500); font-size: 14px; }
        .fee-value { font-weight: 600; font-size: 14px; }

        .timeline-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin: 32px 0;
        }

        .timeline-card {
          background: var(--off-white);
          padding: 28px;
          border-radius: 12px;
          border-top: 4px solid var(--blue-600);
        }

        .timeline-phase {
          font-size: 12px;
          font-weight: 600;
          color: var(--gold-dark);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }

        .timeline-card h3 {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .timeline-list {
          list-style: none;
        }

        .timeline-list li {
          padding: 5px 0;
          font-size: 14px;
          color: var(--gray-500);
        }

        .timeline-list li::before {
          content: "→ ";
          color: var(--blue-600);
          font-weight: 600;
        }

        .disclaimer {
          padding: 32px 0;
          background: var(--off-white);
          border-top: 1px solid var(--gray-200);
        }

        .disclaimer p {
          font-size: 13px;
          color: var(--gray-500);
          text-align: center;
          max-width: 720px;
          margin: 0 auto;
          line-height: 1.7;
        }

        .cta-section {
          padding: 80px 0;
          background: linear-gradient(135deg, var(--blue-600) 0%, var(--blue-700) 100%);
          text-align: center;
        }

        .cta-section h2 {
          font-size: clamp(28px, 4vw, 36px);
          font-weight: 800;
          color: var(--white);
          margin-bottom: 12px;
        }

        .cta-section p {
          color: rgba(255,255,255,0.7);
          font-size: 17px;
          margin-bottom: 32px;
        }

        footer {
          background: var(--gray-900);
          color: var(--white);
          padding: 60px 0 40px;
        }

        .footer-content {
          display: grid;
          grid-template-columns: 1fr 2fr 1fr;
          gap: 40px;
          margin-bottom: 40px;
        }

        .footer-brand h3 {
          font-size: 24px;
          margin-bottom: 12px;
        }

        .footer-brand p {
          color: rgba(255,255,255,0.6);
          font-size: 14px;
          line-height: 1.6;
        }

        .footer-links h4 {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.5);
        }

        .footer-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .footer-grid a {
          color: rgba(255,255,255,0.8);
          text-decoration: none;
          font-size: 14px;
          transition: color 0.2s;
        }

        .footer-grid a:hover {
          color: var(--white);
        }

        .footer-social h4 {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.5);
        }

        .social-links {
          display: flex;
          gap: 12px;
        }

        .social-links a {
          width: 40px;
          height: 40px;
          background: rgba(255,255,255,0.1);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0.6);
          transition: all 0.2s;
        }

        .social-links a:hover {
          background: rgba(255,255,255,0.2);
          color: var(--white);
        }

        .footer-bottom {
          padding-top: 40px;
          border-top: 1px solid rgba(255,255,255,0.1);
          text-align: center;
          color: rgba(255,255,255,0.5);
          font-size: 14px;
        }

        .hero h1 {
          font-size: clamp(36px, 5vw, 52px);
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 24px;
          background: linear-gradient(135deg, var(--gray-900) 0%, var(--blue-600) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* Tokenomics Subsections */
        .token-subsection {
          margin-top: 48px;
          padding-top: 40px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }

        .token-subsection h3 {
          font-size: 22px;
          font-weight: 700;
          color: var(--white);
          margin-bottom: 8px;
        }

        .token-subsection .sub-desc {
          font-size: 15px;
          color: rgba(255,255,255,0.6);
          margin-bottom: 24px;
          line-height: 1.6;
        }

        .token-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 24px;
        }

        .token-table th {
          text-align: left;
          padding: 10px 14px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: rgba(255,255,255,0.5);
          border-bottom: 1px solid rgba(255,255,255,0.15);
        }

        .token-table td {
          padding: 10px 14px;
          font-size: 14px;
          color: rgba(255,255,255,0.8);
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }

        .token-table td:first-child { font-weight: 600; color: var(--white); }

        .token-table .highlight-row td {
          background: rgba(212, 168, 83, 0.08);
          color: var(--gold);
          font-weight: 600;
        }

        .yield-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        .yield-card {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          padding: 20px;
        }

        .yield-card h4 {
          font-size: 14px;
          font-weight: 600;
          color: var(--gold);
          margin-bottom: 6px;
        }

        .yield-card .yield-apy {
          font-size: 24px;
          font-weight: 800;
          color: var(--white);
          margin-bottom: 4px;
        }

        .yield-card p {
          font-size: 13px;
          color: rgba(255,255,255,0.5);
        }

        .burn-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
        }

        .burn-card {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          padding: 18px;
        }

        .burn-card h4 {
          font-size: 14px;
          font-weight: 600;
          color: var(--white);
          margin-bottom: 6px;
        }

        .burn-card p {
          font-size: 13px;
          color: rgba(255,255,255,0.5);
          line-height: 1.5;
        }

        .supply-timeline {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .supply-point {
          text-align: center;
          flex: 1;
          min-width: 80px;
        }

        .supply-point .supply-pct {
          font-size: 22px;
          font-weight: 800;
          color: var(--gold);
        }

        .supply-point .supply-amount {
          font-size: 13px;
          color: rgba(255,255,255,0.7);
          margin-top: 2px;
        }

        .supply-point .supply-time {
          font-size: 12px;
          color: rgba(255,255,255,0.4);
          margin-top: 4px;
        }

        .oracle-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 14px;
        }

        .oracle-card {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          padding: 18px;
          text-align: center;
        }

        .oracle-card .oracle-value {
          font-size: 18px;
          font-weight: 800;
          color: var(--white);
          margin-bottom: 4px;
        }

        .oracle-card .oracle-label {
          font-size: 12px;
          color: rgba(255,255,255,0.5);
        }

        @media (max-width: 768px) {
          .nav-menu, .nav-actions { display: none; }
          .hero { padding: 100px 0 48px; }
          .hero h1 { font-size: 32px; }
          .hero-label { font-size: 11px; white-space: normal; text-align: center; }
          .hero-subtitle { font-size: 16px; }
          .hero-meta { font-size: 12px; }
          .hero p { font-size: 16px; }
          .hero-buttons { flex-direction: column; }
          .mobile-toggle { display: flex; }
          .toc-grid, .stats-grid, .regional-grid { grid-template-columns: repeat(2, 1fr); }
          .two-col, .distribution-grid, .fee-grid, .timeline-grid { grid-template-columns: 1fr; }
          .yield-grid, .burn-grid { grid-template-columns: 1fr !important; }
          .oracle-grid { grid-template-columns: repeat(3, 1fr); }
          .supply-timeline { flex-direction: column; align-items: center; }
          .footer-content { grid-template-columns: 1fr; }
          .footer-grid { grid-template-columns: repeat(2, 1fr); }
          .modal-buttons { flex-direction: column; }
          .section-intro { font-size: 15px; }
        }

        @media (max-width: 480px) {
          .toc-grid, .stats-grid, .regional-grid { grid-template-columns: 1fr; }
        }
      `}</style>

       {/* Modal */}
     <div 
        className={`modal-overlay ${modalOpen ? 'active' : ''}`}
        onClick={() => setModalOpen(false)}
      >
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h3>Join the Waitlist</h3>
          
          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Thank you') ? 'success' : 'error'}`}>
              {submitMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="modal-field">
              <input 
                type="email" 
                name="email"
                placeholder="Enter your email" 
                value={formData.email}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
            </div>
            <div className="modal-field">
              <label>I am a...</label>
              <select 
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              >
                <option value="user">User</option>
                <option value="merchant">Merchant</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="modal-checkbox">
              <input 
                type="checkbox" 
                name="ageConfirm"
                id="ageConfirm" 
                checked={formData.ageConfirm}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
              <span>I am 18 or older and agree to the <a target='_blank' rel='noopener noreferrer' href="/terms">Terms of Service and Privacy Policy</a></span>
            </div>
            <div className="modal-buttons">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
              </button>
              <button 
                type="button"
                onClick={() => setModalOpen(false)} 
                className="btn btn-outline"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

   <Header onJoinWaitlist={() => setModalOpen(true)} />

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-label">Technical Documentation · Patent Pending</div>
          <h1>Rewardly Protocol Whitepaper</h1>
          <p className="hero-subtitle">The first protocol to guarantee reward value at earn time — not redemption time.<br />38+ smart contracts across 7 layers. 120B RWLY tokens. Earn-time commitment architecture on Polygon.</p>
          <p className="hero-meta">Version 3.0 · March 2026 · Built on Polygon · PCT/US26/20246</p>
        </div>
      </section>

      {/* TOC */}
      <section className="toc-section">
        <div className="container-narrow">
          <div className="toc-grid">
            <a href="#market" className="toc-item"><span className="toc-number">01</span> Market</a>
            <a href="#solution" className="toc-item"><span className="toc-number">02</span> Solution</a>
            <a href="#architecture" className="toc-item"><span className="toc-number">03</span> Architecture</a>
            <a href="#tokenomics" className="toc-item"><span className="toc-number">04</span> Tokenomics</a>
            <a href="#regions" className="toc-item"><span className="toc-number">05</span> Regions</a>
            <a href="#business" className="toc-item"><span className="toc-number">06</span> Business</a>
            <a href="#roadmap" className="toc-item"><span className="toc-number">07</span> Roadmap</a>
            <a href="#trust" className="toc-item"><span className="toc-number">08</span> Trust</a>
          </div>
        </div>
      </section>

      {/* Executive Summary */}
      <section style={{padding: '40px 0', background: 'var(--off-white)'}}>
        <div className="container-narrow">
          <div style={{background: 'var(--white)', border: '1px solid var(--gray-200)', borderRadius: '12px', padding: '32px'}}>
            <div style={{fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--blue-600)', marginBottom: '12px'}}>Executive Summary</div>
            <p style={{fontSize: '16px', lineHeight: 1.7, color: 'var(--gray-900)', marginBottom: '16px'}}>
              <strong>Rewardly Protocol</strong> is the first loyalty infrastructure to guarantee reward value at the moment of earning — not redemption. $300 billion in loyalty value is issued globally every year; nearly half is never redeemed. Using a patent-pending earn-time commitment architecture, Rewardly converts loyalty points into RWLY tokens on Polygon with value locked by oracle price at earn time. 120B tokens power a seven-layer smart contract architecture with 38+ smart contracts, serving enterprises, coalitions, and consumers.
            </p>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 24px', marginTop: '4px'}}>
              {[
                {label: '$300B', desc: 'Issued Annually'},
                {label: '~50%', desc: 'Never Redeemed'},
                {label: '120B', desc: 'RWLY Total Supply'},
                {label: '20B', desc: 'Mintable Regional'},
                {label: '4', desc: 'Global Regions'},
                {label: 'Zero', desc: 'POS Integration'},
              ].map((stat, i) => (
                <div key={i} style={{display: 'flex', alignItems: 'baseline', gap: '6px'}}>
                  <span style={{fontSize: '15px', fontWeight: 700, color: 'var(--blue-600)'}}>{stat.label}</span>
                  <span style={{fontSize: '13px', color: 'var(--gray-500)'}}>{stat.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Innovations — moved up from Trust section for visibility */}
      <section style={{padding: '40px 0', background: 'var(--white)'}}>
        <div className="container-narrow">
          <h3 style={{fontSize: '18px', fontWeight: 700, marginBottom: '16px', textAlign: 'center'}}>9 Key Protocol Innovations</h3>
          <div className="stats-grid">
            <div className="stat-card" style={{textAlign: 'left', padding: '20px', borderLeft: '3px solid var(--gold)'}}>
              <div style={{fontWeight: 700, fontSize: '14px', marginBottom: '4px', color: 'var(--gold-dark)'}}>Earn-Time Commitment (Patent Pending)</div>
              <div style={{fontSize: '13px', color: 'var(--gray-500)'}}>Value guaranteed at earning, not redemption. Oracle-priced, immutable, no expiration.</div>
            </div>
            <div className="stat-card" style={{textAlign: 'left', padding: '20px'}}>
              <div style={{fontWeight: 700, fontSize: '14px', marginBottom: '4px'}}>Trust-Locked Treasury</div>
              <div style={{fontSize: '13px', color: 'var(--gray-500)'}}>Tokens exit only through verified conversions</div>
            </div>
            <div className="stat-card" style={{textAlign: 'left', padding: '20px'}}>
              <div style={{fontWeight: 700, fontSize: '14px', marginBottom: '4px'}}>Proof-of-Loyalty Mining</div>
              <div style={{fontSize: '13px', color: 'var(--gray-500)'}}>Earn rewards based on real engagement</div>
            </div>
            <div className="stat-card" style={{textAlign: 'left', padding: '20px'}}>
              <div style={{fontWeight: 700, fontSize: '14px', marginBottom: '4px'}}>Round-Trip Velocity</div>
              <div style={{fontSize: '13px', color: 'var(--gray-500)'}}>Both earning and spending build score</div>
            </div>
            <div className="stat-card" style={{textAlign: 'left', padding: '20px'}}>
              <div style={{fontWeight: 700, fontSize: '14px', marginBottom: '4px'}}>Coalition Gauge Voting</div>
              <div style={{fontSize: '13px', color: 'var(--gray-500)'}}>Token holders vote on reward allocation</div>
            </div>
            <div className="stat-card" style={{textAlign: 'left', padding: '20px'}}>
              <div style={{fontWeight: 700, fontSize: '14px', marginBottom: '4px'}}>Dual Governance</div>
              <div style={{fontSize: '13px', color: 'var(--gray-500)'}}>Separate votes for platform + community</div>
            </div>
            <div className="stat-card" style={{textAlign: 'left', padding: '20px'}}>
              <div style={{fontWeight: 700, fontSize: '14px', marginBottom: '4px'}}>Regional Mintable Supply</div>
              <div style={{fontSize: '13px', color: 'var(--gray-500)'}}>20B reserved as future mintable supply</div>
            </div>
            <div className="stat-card" style={{textAlign: 'left', padding: '20px'}}>
              <div style={{fontWeight: 700, fontSize: '14px', marginBottom: '4px'}}>Industry-Normalized Mining</div>
              <div style={{fontSize: '13px', color: 'var(--gray-500)'}}>Fair rewards across all industries</div>
            </div>
            <div className="stat-card" style={{textAlign: 'left', padding: '20px'}}>
              <div style={{fontWeight: 700, fontSize: '14px', marginBottom: '4px'}}>Impact Mining</div>
              <div style={{fontSize: '13px', color: 'var(--gray-500)'}}>Commercial fees fund non-profit programs</div>
            </div>
          </div>
        </div>
      </section>

      {/* Market */}
      <section className="content-section" id="market">
        <div className="container-narrow">
          <div className="section-number">Section 01</div>
          <h2 className="section-title">Market Opportunity</h2>
          <p className="section-intro">The global loyalty market represents a massive opportunity with critical inefficiencies that blockchain technology is uniquely positioned to solve.</p>
          
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">$300B</div>
              <div className="stat-label">Issued Annually (Global)</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">~50%</div>
              <div className="stat-label">Points Never Redeemed</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">$40B+</div>
              <div className="stat-label">Airline Liabilities Alone</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">14.6%</div>
              <div className="stat-label">Loyalty Tech CAGR</div>
            </div>
          </div>

          <div className="two-col">
            <div className="content-card">
              <h3>Current Challenges</h3>
              <ul>
                <li>Points locked in program silos</li>
                <li>Limited cross-program exchange</li>
                <li>~50% of points never redeemed</li>
                <li>Complex regional operations</li>
                <li>Inconsistent value translation</li>
              </ul>
            </div>
            <div className="content-card">
              <h3>Our Innovation</h3>
              <ul>
                <li>Earn-time token commitment (patent pending)</li>
                <li>AI receipt validation — zero POS integration</li>
                <li>Universal point conversion protocol</li>
                <li>Cross-border loyalty infrastructure</li>
                <li>Real-time settlement on Polygon</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="content-section" id="solution">
        <div className="container-narrow">
          <div className="section-number">Section 02</div>
          <h2 className="section-title">The Rewardly Protocol Solution</h2>
          <p className="section-intro">The first blockchain loyalty protocol to commit token value at earn time — not redemption time. A patent-pending architecture (PCT/US26/20246) enabling seamless, guaranteed conversion between enterprise loyalty points and RWLY tokens, with value locked by oracle price at the moment of purchase validation.</p>

          <div className="two-col">
            <div className="content-card">
              <h3>Universal Point Conversion</h3>
              <ul>
                <li>Instant enterprise point conversion</li>
                <li>Real-time rate determination</li>
                <li>Smart contract settlement</li>
                <li>Value preservation mechanisms</li>
              </ul>
            </div>
            <div className="content-card">
              <h3>Cross-Border Operations</h3>
              <ul>
                <li>Regional market enablement</li>
                <li>Compliance-driven infrastructure</li>
                <li>Automated settlement</li>
                <li>Dynamic rate adjustment</li>
              </ul>
            </div>
            <div className="content-card">
              <h3>Enterprise Integration</h3>
              <ul>
                <li>Seamless program onboarding</li>
                <li>Custom reward structures</li>
                <li>Real-time analytics</li>
                <li>Automated distribution</li>
              </ul>
            </div>
            <div className="content-card">
              <h3>Security Infrastructure</h3>
              <ul>
                <li>Multi-signature controls</li>
                <li>Rate manipulation protection</li>
                <li>Fraud prevention systems</li>
                <li>Real-time monitoring</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section className="content-section" id="architecture">
        <div className="container-narrow">
          <div className="section-number">Section 03</div>
          <h2 className="section-title">Technical Architecture</h2>
          <p className="section-intro">Built on Polygon with multi-chain expansion planned. Seven-layer modular smart contract architecture (38+ smart contracts) designed for security and scalability, with coalition commerce at the heart.</p>

          <div className="two-col">
            <div className="content-card">
              <h3>Seven-Layer Smart Contract Stack</h3>
              <ul>
                <li style={{whiteSpace: 'nowrap'}}>Layer 1: Foundation — Storage & Access</li>
                <li style={{whiteSpace: 'nowrap'}}>Layer 2: Control & Policy — Governance & Pricing</li>
                <li style={{whiteSpace: 'nowrap'}}>Layer 3: Money Core — Tokens, Fees & Treasury</li>
                <li style={{whiteSpace: 'nowrap'}}>Layer 4: Operations — Processing & Settlement</li>
                <li style={{whiteSpace: 'nowrap'}}>Layer 5: Distribution — Rewards & Staking</li>
                <li style={{whiteSpace: 'nowrap'}}>Layer 6: Business Registry — Coalitions & Merchants</li>
                <li style={{whiteSpace: 'nowrap'}}>Layer 7: Market Operations — Internal Crossing & DEX</li>
              </ul>
            </div>
            <div className="content-card">
              <h3>Key Integrations</h3>
              <ul>
                <li style={{whiteSpace: 'nowrap'}}>Chainlink Price Oracles</li>
                <li style={{whiteSpace: 'nowrap'}}>Dual-Adapter Bridge (LayerZero + Wormhole NTT)</li>
                <li style={{whiteSpace: 'nowrap'}}>Enterprise REST APIs</li>
                <li style={{whiteSpace: 'nowrap'}}>Payment Gateway Connectors</li>
                <li style={{whiteSpace: 'nowrap'}}>AI-Powered Receipt Validation</li>
                <li style={{whiteSpace: 'nowrap'}}>Web3Auth Key Management</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Tokenomics */}
      <section className="token-section" id="tokenomics">
        <div className="container-narrow">
          <div className="section-number">Section 04</div>
          <h2 className="section-title">Token Economics</h2>
          
          <div className="token-header">
            <div className="token-total">120 Billion</div>
            <div className="token-label">Total RWLY Supply</div>
            <p style={{fontSize: '15px', color: 'rgba(255,255,255,0.6)', marginTop: '12px', maxWidth: '600px', margin: '12px auto 0'}}>100B minted at genesis + 20B reserved as future mintable supply, released only through governance-approved regional activations.</p>
          </div>

          <div className="distribution-grid">
            <div className="distribution-card">
              <h3>Ecosystem & Community</h3>
              <div className="distribution-percent">35%</div>
              <p className="distribution-desc">35B RWLY — Community rewards, airdrops, mining. Milestone-based release over 5 years, 5% at TGE.</p>
            </div>
            <div className="distribution-card">
              <h3>Enterprise & Integration</h3>
              <div className="distribution-percent">20%</div>
              <p className="distribution-desc">20B RWLY — Coalition anchor sales, merchant onboarding. Per enterprise deal, governance-controlled.</p>
            </div>
            <div className="distribution-card">
              <h3>Team & Development</h3>
              <div className="distribution-percent">15%</div>
              <p className="distribution-desc">15B RWLY — Core team allocation. 4-year vest with 1-year cliff, 0% at TGE.</p>
            </div>
            <div className="distribution-card">
              <h3>Staking Rewards Pool</h3>
              <div className="distribution-percent">10%</div>
              <p className="distribution-desc">10B RWLY — Emission schedule declining 20% annually. Funds veRWLY staking yields.</p>
            </div>
            <div className="distribution-card">
              <h3>DEX Liquidity</h3>
              <div className="distribution-percent">8%</div>
              <p className="distribution-desc">8B RWLY — QuickSwap pool seeding. 40% at TGE, remaining 60% over 12 months.</p>
            </div>
            <div className="distribution-card">
              <h3>Seed / Private Investors</h3>
              <div className="distribution-percent">6%</div>
              <p className="distribution-desc">6B RWLY — SAFT (Reg D). 2-year vest with 6-month cliff, 0% at TGE.</p>
            </div>
            <div className="distribution-card">
              <h3>Cross-Chain & Bridge</h3>
              <div className="distribution-percent">4%</div>
              <p className="distribution-desc">4B RWLY — Reserved for multi-chain expansion. Released per chain activation.</p>
            </div>
            <div className="distribution-card">
              <h3>Strategic Reserve</h3>
              <div className="distribution-percent">2%</div>
              <p className="distribution-desc">2B RWLY — Governance-controlled with 15% quorum requirement. 48hr timelock on releases.</p>
            </div>
          </div>

          {/* veRWLY Staking */}
          <div className="token-subsection">
            <h3>veRWLY Staking Mechanism</h3>
            <p className="sub-desc">Lock RWLY to receive veRWLY — a non-transferable governance token that amplifies staking rewards, reduces protocol fees, and increases earning power.</p>

            <table className="token-table">
              <thead>
                <tr>
                  <th>Lock Duration</th>
                  <th>Multiplier</th>
                  <th>Fee Discount</th>
                  <th>Earning Boost</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1 month</td><td>1.0x</td><td>10%</td><td>1.0x</td></tr>
                <tr><td>3 months</td><td>1.25x</td><td>15%</td><td>1.15x</td></tr>
                <tr><td>6 months</td><td>1.5x</td><td>25%</td><td>1.25x</td></tr>
                <tr><td>12 months</td><td>2.0x</td><td>40%</td><td>1.75x</td></tr>
                <tr className="highlight-row"><td>24 months</td><td>2.5x</td><td>50%</td><td>2.0x</td></tr>
              </tbody>
            </table>

            <p className="sub-desc" style={{marginBottom: '16px'}}>Minimum stake: 10,000 RWLY · 7-day cooldown after unlock · Early unstake penalty: 5-20% (default 10%)</p>

            <h3 style={{marginTop: '32px'}}>Three-Layer Yield Model</h3>
            <div className="yield-grid">
              <div className="yield-card">
                <h4>Layer 1: Fee Yield</h4>
                <div className="yield-apy">1-4% APY</div>
                <p>Protocol fee revenue via FeeManager. Fully sustainable.</p>
              </div>
              <div className="yield-card">
                <h4>Layer 2: Emission</h4>
                <div className="yield-apy">4-6% APY</div>
                <p>10B pool + 4B mintable, declining 20%/year. Time-limited.</p>
              </div>
              <div className="yield-card">
                <h4>Layer 3: Lock Boost</h4>
                <div className="yield-apy">1.0-2.5x</div>
                <p>Redistribution from short to long lockers. Zero cost.</p>
              </div>
            </div>
          </div>

          {/* Emission Schedule */}
          <div className="token-subsection">
            <h3>Staking Emission Schedule</h3>
            <p className="sub-desc">10B RWLY pool declining 20% annually. Critical transition at Year 3: target 50% of staking rewards funded by real fee revenue.</p>

            <table className="token-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Annual Emission</th>
                  <th>Monthly</th>
                  <th>Pool Remaining</th>
                  <th>Fee Target</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>1</td><td>2.50B</td><td>~208M</td><td>7.50B</td><td>10%</td></tr>
                <tr><td>2</td><td>2.00B</td><td>~167M</td><td>5.50B</td><td>30%</td></tr>
                <tr className="highlight-row"><td>3</td><td>1.60B</td><td>~133M</td><td>3.90B</td><td>50%</td></tr>
                <tr><td>4</td><td>1.28B</td><td>~107M</td><td>2.62B</td><td>70%</td></tr>
                <tr><td>5</td><td>1.02B</td><td>~85M</td><td>1.60B</td><td>80%</td></tr>
                <tr><td>6</td><td>0.82B</td><td>~68M</td><td>0.78B</td><td>90%+</td></tr>
                <tr><td>7+</td><td>0.78B</td><td>~65M</td><td>0.00B</td><td>100%</td></tr>
              </tbody>
            </table>
          </div>

          {/* Deflationary Mechanics */}
          <div className="token-subsection">
            <h3>Deflationary Mechanics</h3>
            <p className="sub-desc">Six burn mechanisms ensure RWLY supply decreases over time.</p>

            <div className="burn-grid">
              <div className="burn-card">
                <h4>Revenue Buyback</h4>
                <p>10% of all protocol revenue used to buy and burn RWLY permanently.</p>
              </div>
              <div className="burn-card">
                <h4>Transaction Burns</h4>
                <p>0.05% of all on-chain volume burned on every transaction.</p>
              </div>
              <div className="burn-card">
                <h4>Early Unstake Burns</h4>
                <p>50% of early unstake penalty tokens are burned.</p>
              </div>
              <div className="burn-card">
                <h4>Expired Escrow</h4>
                <p>Unclaimed enterprise escrow tokens are permanently burned.</p>
              </div>
              <div className="burn-card">
                <h4>Cross-Coalition Fees</h4>
                <p>Portion of the 0.5% cross-coalition redemption fee is burned.</p>
              </div>
              <div className="burn-card">
                <h4>Bidirectional Burns</h4>
                <p>Portion of RWLY-to-points conversion returns is burned.</p>
              </div>
            </div>
          </div>

          {/* Circulating Supply */}
          <div className="token-subsection">
            <h3>Circulating Supply Projection</h3>
            <p className="sub-desc">Gradual unlock schedule ensures long-term alignment between team, investors, and community.</p>

            <div className="supply-timeline">
              <div className="supply-point">
                <div className="supply-pct">5.4%</div>
                <div className="supply-amount">~5.4B</div>
                <div className="supply-time">TGE</div>
              </div>
              <div className="supply-point">
                <div className="supply-pct">11%</div>
                <div className="supply-amount">~11B</div>
                <div className="supply-time">Month 6</div>
              </div>
              <div className="supply-point">
                <div className="supply-pct">20%</div>
                <div className="supply-amount">~20B</div>
                <div className="supply-time">Month 12</div>
              </div>
              <div className="supply-point">
                <div className="supply-pct">38%</div>
                <div className="supply-amount">~38B</div>
                <div className="supply-time">Month 24</div>
              </div>
              <div className="supply-point">
                <div className="supply-pct">70%</div>
                <div className="supply-amount">~70B</div>
                <div className="supply-time">Month 48</div>
              </div>
              <div className="supply-point">
                <div className="supply-pct">82%</div>
                <div className="supply-amount">~82B</div>
                <div className="supply-time">Month 60</div>
              </div>
            </div>
          </div>

          {/* Dollar-Parity Oracle */}
          <div className="token-subsection">
            <h3>Dollar-Parity Conversion Oracle</h3>
            <p className="sub-desc">Enterprise-grade price stability for predictable point-to-RWLY conversions.</p>

            <div className="oracle-grid">
              <div className="oracle-card">
                <div className="oracle-value">$0.01</div>
                <div className="oracle-label">Anchor (1pt = $0.01)</div>
              </div>
              <div className="oracle-card">
                <div className="oracle-value">10</div>
                <div className="oracle-label">Floor (RWLY/pt)</div>
              </div>
              <div className="oracle-card">
                <div className="oracle-value">1,000</div>
                <div className="oracle-label">Ceiling (RWLY/pt)</div>
              </div>
              <div className="oracle-card">
                <div className="oracle-value">24h</div>
                <div className="oracle-label">TWAP Update</div>
              </div>
              <div className="oracle-card">
                <div className="oracle-value">&plusmn;20%</div>
                <div className="oracle-label">Circuit Breaker</div>
              </div>
            </div>
          </div>

          {/* Proof-of-Loyalty Mining */}
          <div className="token-subsection">
            <h3>Proof-of-Loyalty Mining Score</h3>
            <p className="sub-desc">A multi-dimensional scoring system that rewards genuine economic activity — not just capital. Mining Score determines RWLY earning rates across all protocol interactions.</p>

            <h4 style={{fontSize: '16px', fontWeight: 600, color: 'var(--gold)', marginBottom: '16px'}}>Score Components</h4>
            <table className="token-table">
              <thead>
                <tr>
                  <th>Dimension</th>
                  <th>Weight</th>
                  <th>What It Measures</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Transaction Velocity</td><td>30%</td><td>Frequency and consistency of spending activity</td></tr>
                <tr><td>Cross-Merchant Activity</td><td>25%</td><td>Diversity of merchants visited within a coalition</td></tr>
                <tr><td>Redemption Rate</td><td>20%</td><td>Ratio of RWLY earned to RWLY redeemed (round-trip velocity)</td></tr>
                <tr><td>Coalition Engagement</td><td>15%</td><td>Participation in promotions, referrals, and coalition events</td></tr>
                <tr><td>Tenure</td><td>10%</td><td>Duration of active participation in the protocol</td></tr>
              </tbody>
            </table>

            <h4 style={{fontSize: '16px', fontWeight: 600, color: 'var(--gold)', marginBottom: '16px', marginTop: '32px'}}>Industry Normalization Factors</h4>
            <p className="sub-desc">To ensure fairness across industries with different transaction frequencies and values, Mining Scores are normalized per industry:</p>
            <div className="burn-grid">
              <div className="burn-card">
                <h4>Airlines</h4>
                <p>15x normalization factor. High-value, low-frequency transactions.</p>
              </div>
              <div className="burn-card">
                <h4>Healthcare</h4>
                <p>10x normalization. Infrequent but high-value interactions.</p>
              </div>
              <div className="burn-card">
                <h4>Hotels</h4>
                <p>8x normalization. Seasonal, mid-frequency transactions.</p>
              </div>
              <div className="burn-card">
                <h4>Retail</h4>
                <p>1x baseline. High-frequency, low-to-mid value transactions.</p>
              </div>
              <div className="burn-card">
                <h4>F&amp;B / Dining</h4>
                <p>1.5x normalization. Frequent, lower-value transactions.</p>
              </div>
              <div className="burn-card">
                <h4>Automotive</h4>
                <p>12x normalization. Very high-value, very low-frequency.</p>
              </div>
            </div>
            <p className="sub-desc" style={{marginTop: '16px'}}>This ensures a hotel guest staying once per quarter earns comparably to a retail customer shopping weekly — rewarding loyalty, not just volume.</p>
          </div>

          {/* Future Mintable Supply */}
          <div className="token-subsection">
            <h3>Future Mintable Supply (20B)</h3>
            <p className="sub-desc">20B RWLY tokens are reserved as future mintable supply. These are not pre-minted — they can only be created through governance-approved regional activation events.</p>

            <table className="token-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Allocation</th>
                  <th>Annual Cap</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>APAC Regional Mining</td><td>2.5B</td><td>500M/yr</td></tr>
                <tr><td>Europe Regional Mining</td><td>2.0B</td><td>400M/yr</td></tr>
                <tr><td>Americas Regional Mining</td><td>2.0B</td><td>400M/yr</td></tr>
                <tr><td>MEA Regional Mining</td><td>1.5B</td><td>300M/yr</td></tr>
                <tr><td>Program Integration</td><td>6.0B</td><td>1.2B/yr</td></tr>
                <tr><td>Staking Reward Supplement</td><td>4.0B</td><td>800M/yr</td></tr>
                <tr><td>Strategic Reserve</td><td>2.0B</td><td>400M/yr</td></tr>
              </tbody>
            </table>
            <p className="sub-desc" style={{marginTop: '16px'}}>The 20B mintable supply is divided into 7 categories — 4 regional mining pools and 3 protocol-level reserves — each with governance-enforced annual caps to ensure controlled, sustainable distribution.</p>
          </div>

          {/* Stress Tests */}
          <div className="token-subsection">
            <h3>Stress Test Scenarios</h3>
            <p className="sub-desc">The protocol is designed to withstand extreme market and operational conditions. Seven stress scenarios have been modeled with built-in mitigation mechanisms.</p>

            <div className="burn-grid">
              <div className="burn-card">
                <h4>Bear Market (90% Drop)</h4>
                <p>Circuit breaker halts conversions at &plusmn;20%. Treasury runway sustains operations for 16+ years at floor price.</p>
              </div>
              <div className="burn-card">
                <h4>Anchor Dump</h4>
                <p>Enterprise vesting (4yr + 1yr cliff) and 48hr timelock on treasury releases prevent sudden large-scale sells.</p>
              </div>
              <div className="burn-card">
                <h4>Zero Revenue (12mo)</h4>
                <p>Staking emission pool sustains network for 7 years even with zero fee revenue. Critical transition at Year 3.</p>
              </div>
              <div className="burn-card">
                <h4>Regulatory Shutdown</h4>
                <p>Regional isolation: one region can be paused without affecting others. User funds always withdrawable.</p>
              </div>
              <div className="burn-card">
                <h4>Smart Contract Exploit</h4>
                <p>Emergency pause, multi-sig recovery, insurance pool. OpenZeppelin access control + Chainlink oracle validation.</p>
              </div>
              <div className="burn-card">
                <h4>Oracle Manipulation</h4>
                <p>TWAP over 24h window, Chainlink multi-source feeds, rate staleness enforcement, flash loan protection.</p>
              </div>
            </div>
          </div>

          {/* Protocol Maturity */}
          <div className="token-subsection">
            <h3>Protocol Maturity</h3>
            <p className="sub-desc">Current development and audit status, verified against the internal engineering dashboard.</p>
            <div style={{background:'#0F172A',borderRadius:'12px',padding:'24px',fontFamily:'monospace',fontSize:'13px',lineHeight:'1.8',color:'#94A3B8',marginTop:'16px'}}>
              <div><span style={{color:'#4ADE80'}}>✓</span> <span style={{color:'#E2E8F0'}}>202 Foundry tests passing</span></div>
              <div><span style={{color:'#4ADE80'}}>✓</span> <span style={{color:'#E2E8F0'}}>84 supply invariant checks across 16 token lifecycle phases</span></div>
              <div><span style={{color:'#4ADE80'}}>✓</span> <span style={{color:'#E2E8F0'}}>Internal security audit complete (Wave 1 + Wave 2, 52 findings remediated)</span></div>
              <div><span style={{color:'#4ADE80'}}>✓</span> <span style={{color:'#E2E8F0'}}>Amoy testnet deployment pipeline validated</span></div>
              <div><span style={{color:'#FBBF24'}}>→</span> <span style={{color:'#E2E8F0'}}>External third-party audit: scheduled pre-mainnet</span></div>
              <div><span style={{color:'#FBBF24'}}>→</span> <span style={{color:'#E2E8F0'}}>Mainnet deployment: post-audit</span></div>
            </div>
          </div>

          {/* TGE Security */}
          <div className="token-subsection">
            <h3>TGE Security Roadmap</h3>
            <p className="sub-desc">Token Generation Event follows a phased security approach. Four P0 blockers must be resolved before any tokens are minted.</p>

            <table className="token-table">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Requirement</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr className="highlight-row"><td>P0</td><td>Flash loan protection on all conversion paths</td><td>Implemented</td></tr>
                <tr className="highlight-row"><td>P0</td><td>Rate limiting per wallet per epoch</td><td>Implemented</td></tr>
                <tr className="highlight-row"><td>P0</td><td>Oracle staleness enforcement (&lt;24h)</td><td>Implemented</td></tr>
                <tr className="highlight-row"><td>P0</td><td>KYC tier restriction on conversion amounts</td><td>Implemented</td></tr>
                <tr><td>P1</td><td>Chainlink integration for external price feeds</td><td>Implemented</td></tr>
                <tr><td>P1</td><td>TWAP (24h) for internal pricing</td><td>Implemented</td></tr>
                <tr><td>P2</td><td>Real-time monitoring &amp; anomaly detection</td><td>Post-Launch</td></tr>
                <tr><td>P2</td><td>Circuit breakers (&plusmn;20% daily band)</td><td>Implemented</td></tr>
                <tr><td>P3</td><td>Bug bounty program (up to $100K)</td><td>Month 3+</td></tr>
                <tr><td>P3</td><td>Third-party security audit</td><td>Pre-Mainnet</td></tr>
              </tbody>
            </table>
          </div>

          {/* Compliance */}
          <div className="token-subsection">
            <h3>Compliance Framework</h3>
            <p className="sub-desc">RWLY is designed as a utility token enabling point conversion and commerce. The protocol employs a comprehensive compliance architecture.</p>

            <div className="yield-grid">
              <div className="yield-card">
                <h4>Howey Test Analysis</h4>
                <div className="yield-apy" style={{fontSize: '16px'}}>Utility Token</div>
                <p>RWLY derives value from use within the protocol (point conversion, staking, governance) — not from expectation of profit from others' efforts.</p>
              </div>
              <div className="yield-card">
                <h4>KYC Architecture</h4>
                <div className="yield-apy" style={{fontSize: '16px'}}>3-Tier</div>
                <p>Tier 1: Basic (email, $500/mo). Tier 2: Standard (ID, $5K/mo). Tier 3: Enhanced (full KYC, unlimited).</p>
              </div>
              <div className="yield-card">
                <h4>Jurisdictional Strategy</h4>
                <div className="yield-apy" style={{fontSize: '16px'}}>Multi-Region</div>
                <p>Singapore MAS licensing (primary). Regional compliance per activation. US excluded at TGE pending regulatory clarity.</p>
              </div>
            </div>

            <h4 style={{fontSize: '16px', fontWeight: 600, color: 'var(--gold)', marginBottom: '16px', marginTop: '32px'}}>Investor Protections</h4>
            <div className="burn-grid" style={{gridTemplateColumns: 'repeat(2, 1fr)'}}>
              <div className="burn-card">
                <h4>Seed / Private (SAFT)</h4>
                <p>Reg D compliant. 2-year vesting with 6-month cliff. 0% at TGE. Pro-rata quarterly unlock after cliff.</p>
              </div>
              <div className="burn-card">
                <h4>Team Allocation</h4>
                <p>4-year vesting with 1-year cliff. 0% at TGE. Ensures long-term alignment between team and protocol.</p>
              </div>
              <div className="burn-card">
                <h4>Strategic Reserve</h4>
                <p>2B RWLY under governance control. 15% quorum requirement. 48hr timelock on all releases.</p>
              </div>
              <div className="burn-card">
                <h4>Anti-Rug Mechanics</h4>
                <p>All admin privileges are explicit, role-based, and auditable on-chain. Total supply capped at 120B, enforced by code.</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Regions */}
      <section className="content-section" id="regions">
        <div className="container-narrow">
          <div className="section-number">Section 05</div>
          <h2 className="section-title">Regional Structure</h2>
          <p className="section-intro">Four global regions with tailored multipliers ensure equitable access and sustainable growth across diverse economies.</p>

          <div className="regional-grid">
            <div className="regional-card">
              <div className="regional-name">APAC</div>
              <div className="regional-multiplier">1.5x</div>
            </div>
            <div className="regional-card">
              <div className="regional-name">Europe</div>
              <div className="regional-multiplier">1.3x</div>
            </div>
            <div className="regional-card">
              <div className="regional-name">Americas</div>
              <div className="regional-multiplier">1.4x</div>
            </div>
            <div className="regional-card">
              <div className="regional-name">MEA</div>
              <div className="regional-multiplier">1.6x</div>
            </div>
          </div>

          <p style={{textAlign: 'center', color: 'var(--gray-500)', fontSize: '14px', marginTop: '24px'}}>Regional multipliers optimize rewards based on local market conditions and encourage adoption in emerging economies.</p>
        </div>
      </section>

      {/* Business Model */}
      <section className="content-section" id="business">
        <div className="container-narrow">
          <div className="section-number">Section 06</div>
          <h2 className="section-title">Business Model</h2>
          <p className="section-intro">Coalition-as-a-Service revenue model with four streams — treasury sales, conversion fees, redemption fees, and analytics SaaS.</p>

          <h3 style={{fontSize: '18px', fontWeight: 700, marginBottom: '16px', marginTop: '32px'}}>Fee Structure by Coalition Tier</h3>
          <div className="fee-grid">
            <div className="fee-card">
              <h3>Tier 1: Anchor</h3>
              <div className="fee-row">
                <span className="fee-name">Point-to-RWLY</span>
                <span className="fee-value">0.05 - 0.15%</span>
              </div>
              <div className="fee-row">
                <span className="fee-name">RWLY-to-Points</span>
                <span className="fee-value">0.3 - 0.5%</span>
              </div>
              <div className="fee-row">
                <span className="fee-name">Cross-Coalition</span>
                <span className="fee-value">0.3%</span>
              </div>
              <div className="fee-row">
                <span className="fee-name">Cross-Border</span>
                <span className="fee-value">0.2 - 0.3%</span>
              </div>
            </div>
            <div className="fee-card">
              <h3>Tier 2: Standard</h3>
              <div className="fee-row">
                <span className="fee-name">Point-to-RWLY</span>
                <span className="fee-value">0.1 - 0.3%</span>
              </div>
              <div className="fee-row">
                <span className="fee-name">RWLY-to-Points</span>
                <span className="fee-value">N/A</span>
              </div>
              <div className="fee-row">
                <span className="fee-name">Cross-Coalition</span>
                <span className="fee-value">0.5%</span>
              </div>
              <div className="fee-row">
                <span className="fee-name">Cross-Border</span>
                <span className="fee-value">0.3 - 0.5%</span>
              </div>
            </div>
          </div>

          <h3 style={{fontSize: '18px', fontWeight: 700, marginBottom: '16px', marginTop: '40px'}}>Launch Verticals &amp; Market Opportunity</h3>
          <p className="section-intro" style={{marginBottom: '24px'}}>Two-vertical strategy: Shopping Malls (APAC Focus) + Employer Rewards Programs. Targeting high-density, multi-tenant ecosystems first.</p>

          <div className="stats-grid" style={{marginBottom: '24px'}}>
            <div className="stat-card">
              <div className="stat-value">116K+</div>
              <div className="stat-label">Malls Globally</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">5,900+</div>
              <div className="stat-label">University Campuses</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">17,600+</div>
              <div className="stat-label">Airports Worldwide</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">$8-12B</div>
              <div className="stat-label">SAM (5% penetration)</div>
            </div>
          </div>

          <h3 style={{fontSize: '18px', fontWeight: 700, marginBottom: '16px', marginTop: '40px'}}>Fee Distribution (Protocol Level)</h3>
          <div className="fee-grid">
            <div className="fee-card">
              <div className="fee-row"><span className="fee-name">Liquidity Pool</span><span className="fee-value">30%</span></div>
              <div className="fee-row"><span className="fee-name">Treasury Reserve</span><span className="fee-value">28%</span></div>
              <div className="fee-row"><span className="fee-name">Staking Rewards</span><span className="fee-value">20%</span></div>
            </div>
            <div className="fee-card">
              <div className="fee-row"><span className="fee-name">Regional Development</span><span className="fee-value">10%</span></div>
              <div className="fee-row"><span className="fee-name">Protocol Maintenance</span><span className="fee-value">10%</span></div>
              <div className="fee-row"><span className="fee-name">Impact Pool</span><span className="fee-value">2%</span></div>
            </div>
          </div>
          <p style={{marginTop: '16px', fontSize: '14px', color: 'var(--gray-500)', fontStyle: 'italic'}}>Note: 10% of total protocol revenue is directed to Revenue Buyback & Burn, funded from the Treasury Reserve allocation.</p>
        </div>
      </section>

      {/* Roadmap */}
      <section className="content-section" id="roadmap" style={{background: 'var(--off-white)'}}>
        <div className="container-narrow">
          <div className="section-number">Section 07</div>
          <h2 className="section-title">Implementation Roadmap</h2>

          <div className="timeline-grid">
            <div className="timeline-card">
              <div className="timeline-phase">Now - Q2 2026</div>
              <h3>Foundation</h3>
              <ul className="timeline-list">
                <li>Core protocol deployment</li>
                <li>Token generation event</li>
                <li>Initial enterprise onboarding</li>
                <li>User wallet launch</li>
                <li>Basic merchant network</li>
              </ul>
            </div>
            <div className="timeline-card">
              <div className="timeline-phase">Q3 2026 - Q1 2027</div>
              <h3>Growth</h3>
              <ul className="timeline-list">
                <li>Advanced platform features</li>
                <li>Merchant/Enterprise network expansion</li>
                <li>Cross-chain bridges</li>
                <li>Staking program launch</li>
                <li>Governance activation</li>
              </ul>
            </div>
            <div className="timeline-card">
              <div className="timeline-phase">2027+</div>
              <h3>Scale</h3>
              <ul className="timeline-list">
                <li>Full regional coverage</li>
                <li>DAO governance transition</li>
                <li>Complete interoperability</li>
                <li>Enterprise self-service</li>
                <li>Advanced commerce features</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Governance */}
      <section className="content-section" id="trust">
        <div className="container-narrow">
          <div className="section-number">Section 08</div>
          <h2 className="section-title">Trust Framework & Governance</h2>
          <p className="section-intro">Every layer of Rewardly Protocol upholds our commitment to transparency, fairness, and human-centric design.</p>

          <div className="two-col">
            <div className="content-card">
              <h3>Core Principles</h3>
              <ul>
                <li>Immutable & auditable contracts</li>
                <li>Transparent supply management</li>
                <li>All admin privileges are explicit, role-based, and auditable on-chain</li>
                <li>Anti-greed tokenomics</li>
              </ul>
            </div>
            <div className="content-card">
              <h3>Security Standards</h3>
              <ul>
                <li>Industry-standard access controls</li>
                <li>Real-time price verification</li>
                <li>Multi-party approval for key decisions</li>
                <li>Emergency safeguards built in</li>
              </ul>
            </div>
          </div>

          <h3 style={{fontSize: '18px', fontWeight: 700, marginBottom: '16px', marginTop: '40px'}}>Progressive Decentralization</h3>
          <div className="timeline-grid">
            <div className="timeline-card" style={{border: '1px solid var(--gray-200)', borderRadius: '12px', padding: '24px'}}>
              <div className="timeline-phase">Months 1-12</div>
              <h3>Foundation</h3>
              <p style={{color: 'var(--gray-500)', fontSize: '14px'}}>Core team manages operations for speed and security during launch.</p>
            </div>
            <div className="timeline-card" style={{border: '1px solid var(--gray-200)', borderRadius: '12px', padding: '24px'}}>
              <div className="timeline-phase">Months 13-24</div>
              <h3>Guided DAO</h3>
              <p style={{color: 'var(--gray-500)', fontSize: '14px'}}>Community voting activated with team oversight. Token holders begin directing protocol decisions.</p>
            </div>
            <div className="timeline-card" style={{border: '1px solid var(--gray-200)', borderRadius: '12px', padding: '24px'}}>
              <div className="timeline-phase">Month 25+</div>
              <h3>Full DAO</h3>
              <p style={{color: 'var(--gray-500)', fontSize: '14px'}}>Full community governance with no overrides. Fully decentralized protocol and ecosystem decisions.</p>
            </div>
          </div>

          <h3 style={{fontSize: '18px', fontWeight: 700, marginBottom: '8px', marginTop: '32px'}}>Dual Governance Domains</h3>
          <div className="two-col" style={{marginTop: '16px'}}>
            <div className="content-card">
              <h3>Protocol Governance</h3>
              <ul>
                <li>Reward rates & fee structures</li>
                <li>Platform upgrades</li>
                <li>Treasury releases</li>
                <li>Weighted by tokens staked</li>
              </ul>
            </div>
            <div className="content-card">
              <h3>Ecosystem Governance</h3>
              <ul>
                <li>Reward catalog curation</li>
                <li>Partner & coalition approvals</li>
                <li>Community program decisions</li>
                <li>Weighted by activity & participation</li>
              </ul>
            </div>
          </div>

          <p style={{textAlign: 'center', marginTop: '32px'}}>
            <a href="/trust-framework" style={{color: 'var(--blue-600)', fontWeight: '600', textDecoration: 'none'}}>Read the full Trust Framework Documentation &rarr;</a>
          </p>

        </div>
      </section>

      {/* Disclaimer */}
      <section className="disclaimer">
        <div className="container-narrow">
          <p>This whitepaper outlines Rewardly Protocol's vision for a universal point conversion system. The RWLY token is designed as a utility token enabling point conversion and commerce within the Rewardly Protocol ecosystem. It is not intended as an investment vehicle. Users should carefully review all risks before engaging with the platform.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Learn More?</h2>
          <p>Join our waitlist for early access and updates.</p>
          <div style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap'}}>
            <button onClick={() => setModalOpen(true)} className="btn btn-gold" style={{padding: '16px 32px', fontSize: '16px'}}>Join Waitlist →</button>
            <a href="/platform" style={{display: 'inline-flex', alignItems: 'center', padding: '16px 32px', fontSize: '16px', borderRadius: '8px', fontWeight: 600, textDecoration: 'none', border: '1px solid rgba(255,255,255,0.3)', color: '#FFFFFF', transition: 'all 0.2s'}}>Explore the Platform →</a>
          </div>
        </div>
      </section>

      {/* Footer */}
     <Footer />
    </div>
  );
};

export default WhitepaperPage;